import React, { useState, useEffect, useRef, useMemo } from 'react';
import { X, ClipboardList, CheckCircle, AlertCircle, Search, User, Loader2 } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { userProfileService } from '../../../../services/userProfileService';
import { countingListCreationService } from '../../../../services/countingListCreationService';
import {
    ItemBinCountingRow,
    CountingListSelection,
    CountingListSelectionPreview,
    CountingListWarehouseSummary,
    CreateCountingListsResponse,
    CreatedCountingListInfo
} from '../../../../types/countingListCreation';
import { UserProfileDto } from '../../../../types/userProfile';
import { NumericInput } from '../../../common/inputs';

interface CreateCountingListModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Eksplisitt valgte rader, eller «alle rader som matcher filteret». */
    selection: CountingListSelection;
    onSuccess: () => void;
}

/**
 * Oppretter én eller flere tellelister fra utvalget. Tellelister er lagerbundet, så utvalg som
 * spenner flere lager gir én liste per lager. Store utvalg kan deles i lister på maks N linjer.
 * Forhåndsvisningen (rader, unike varer, fordeling per lager) hentes fra server i filtermodus.
 */
export const CreateCountingListModal: React.FC<CreateCountingListModalProps> = ({
    isOpen,
    onClose,
    selection,
    onSuccess
}) => {
    const { t } = useTranslation();

    const [description, setDescription] = useState('');
    const [selectedOperatorId, setSelectedOperatorId] = useState('');
    const [selectedOperatorName, setSelectedOperatorName] = useState('');
    const [profiles, setProfiles] = useState<UserProfileDto[]>([]);
    const [operatorSearch, setOperatorSearch] = useState('');
    const [showOperatorDropdown, setShowOperatorDropdown] = useState(false);
    const [maxLinesPerList, setMaxLinesPerList] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [result, setResult] = useState<CreateCountingListsResponse | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // Forhåndsvisning av utvalget
    const [preview, setPreview] = useState<CountingListSelectionPreview | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);

    const operatorSearchRef = useRef<HTMLInputElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Load profiles from Profile table
    useEffect(() => {
        const loadProfiles = async () => {
            try {
                const allProfiles = await userProfileService.getAllProfilesForCompany();
                setProfiles(allProfiles);
            } catch (err) {
                console.error('Error loading profiles:', err);
            }
        };

        if (isOpen) {
            loadProfiles();
        }
    }, [isOpen]);

    // Forhåndsvisning: radmodus beregnes lokalt, filtermodus hentes fra server
    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;

        if (selection.mode === 'rows') {
            setPreview(buildLocalPreview(selection.rows));
            setPreviewError(null);
            return;
        }

        setPreview(null);
        setPreviewError(null);
        setPreviewLoading(true);
        countingListCreationService.previewByFilter(selection.filters, selection.excludeRowKeys)
            .then(p => { if (!cancelled) setPreview(p); })
            .catch((err: unknown) => {
                if (cancelled) return;
                console.error('Error loading selection preview:', err);
                setPreviewError(t('manager.modules.countingListCreation.bulk.previewError', {}, 'Kunne ikke hente oversikt over utvalget.'));
            })
            .finally(() => { if (!cancelled) setPreviewLoading(false); });

        return () => { cancelled = true; };
    }, [isOpen, selection, t]);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setShowOperatorDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter profiles by search term (UserID, ProfileName, ProfileID, Operator)
    const filteredProfiles = useMemo(() => {
        if (!operatorSearch.trim()) return profiles;
        const search = operatorSearch.toLowerCase();
        return profiles.filter(p =>
            p.userID.toLowerCase().includes(search) ||
            (p.profileName && p.profileName.toLowerCase().includes(search)) ||
            p.profileID.toLowerCase().includes(search) ||
            (p.operator && p.operator.toLowerCase().includes(search))
        );
    }, [profiles, operatorSearch]);

    const maxLines = useMemo(() => {
        const n = parseInt(maxLinesPerList, 10);
        return Number.isFinite(n) && n > 0 ? n : undefined;
    }, [maxLinesPerList]);

    // Antall lister som opprettes: én per lager, delt på maks linjer
    const listsToCreate = useMemo(() => {
        if (!preview) return 0;
        return preview.warehouses.reduce(
            (sum, w) => sum + (maxLines ? Math.ceil(w.rows / maxLines) : 1),
            0
        );
    }, [preview, maxLines]);

    const handleSelectOperator = (profile: UserProfileDto) => {
        setSelectedOperatorId(profile.userID);
        setSelectedOperatorName(profile.profileName
            ? `${profile.userID} - ${profile.profileName}`
            : profile.userID);
        setOperatorSearch('');
        setShowOperatorDropdown(false);
    };

    const handleClearOperator = () => {
        setSelectedOperatorId('');
        setSelectedOperatorName('');
        setOperatorSearch('');
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setErrorMessage(null);

        try {
            const common = {
                description: description.trim() || undefined,
                operatorID: selectedOperatorId || undefined,
                maxLinesPerList: maxLines
            };

            const response = selection.mode === 'filter'
                ? await countingListCreationService.createByFilter({
                    ...common,
                    filters: selection.filters,
                    excludeRowKeys: selection.excludeRowKeys
                })
                : await countingListCreationService.createFromLines({
                    ...common,
                    lines: selection.rows.map(row => ({
                        itemNo: row.itemNo,
                        binCode: row.binCode,
                        warehouseID: row.warehouseID,
                        expectedQuantity: row.binQuantity ?? 0
                    }))
                });

            if (response.success || response.lists.length > 0) {
                // Også delvis suksess vises som resultat — listene som ble opprettet finnes faktisk
                setResult(response);
            } else {
                setErrorMessage(response.errorMessage || t('manager.modules.countingListCreation.modal.unknownError', {}, 'Ukjent feil ved opprettelse'));
            }
        } catch (err: any) {
            setErrorMessage(err.message || t('manager.modules.countingListCreation.modal.createError', {}, 'Feil ved opprettelse av telleliste'));
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    const formatNumber = (n: number) => n.toLocaleString('nb-NO');
    const canSubmit = !isSubmitting && !previewLoading && !!preview && preview.totalRows > 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <div className="flex items-center gap-2">
                        <ClipboardList className="w-5 h-5 text-blue-600" />
                        <h2 className="text-lg font-semibold text-gray-900">{t('manager.modules.countingListCreation.modal.title', {}, 'Opprett telleliste')}</h2>
                    </div>
                    <button
                        onClick={result ? onSuccess : onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="px-6 py-4 space-y-4 overflow-y-auto">
                    {result ? (
                        <ResultView result={result} t={t} formatNumber={formatNumber} onClose={onSuccess} />
                    ) : (
                        <>
                            {/* Error message */}
                            {errorMessage && (
                                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                                    <span className="text-sm text-red-700">{errorMessage}</span>
                                </div>
                            )}

                            {/* Summary */}
                            <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 space-y-2">
                                {selection.mode === 'filter' && (
                                    <p className="text-blue-700 font-medium">
                                        {t('manager.modules.countingListCreation.bulk.filterModeInfo', {}, 'Alle rader som matcher filteret i tabellen')}
                                        {selection.excludeRowKeys.length > 0 && (
                                            <span className="font-normal text-gray-600">
                                                {' '}{t('manager.modules.countingListCreation.bulk.excludedCount', { '0': selection.excludeRowKeys.length }, `(${selection.excludeRowKeys.length} fjernet manuelt)`)}
                                            </span>
                                        )}
                                    </p>
                                )}

                                {previewLoading && (
                                    <p className="flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        {t('manager.modules.countingListCreation.bulk.previewLoading', {}, 'Henter oversikt over utvalget...')}
                                    </p>
                                )}
                                {previewError && <p className="text-red-600">{previewError}</p>}

                                {preview && (
                                    <>
                                        <p><span className="font-medium">{formatNumber(preview.totalRows)}</span> {t('manager.modules.countingListCreation.modal.itemBinLinesSelected', {}, 'vare-/lokasjonslinjer valgt')}</p>
                                        <p><span className="font-medium">{formatNumber(preview.uniqueItems)}</span> {t('manager.modules.countingListCreation.modal.uniqueItems', {}, 'unike varer')}</p>

                                        {preview.warehouses.length === 1 && (
                                            <p>{t('manager.modules.countingListCreation.modal.warehouseLabel', {}, 'Lagersted:')} <span className="font-medium">{preview.warehouses[0].warehouseID}</span></p>
                                        )}

                                        {preview.warehouses.length > 1 && (
                                            <div className="space-y-1">
                                                <p className="text-amber-700">
                                                    {t('manager.modules.countingListCreation.bulk.onePerWarehouse', { '0': preview.warehouses.length }, `Utvalget dekker ${preview.warehouses.length} lager. Det opprettes én telleliste per lager.`)}
                                                </p>
                                                <WarehouseBreakdown warehouses={preview.warehouses} t={t} formatNumber={formatNumber} />
                                            </div>
                                        )}

                                        {preview.totalRows === 0 && (
                                            <p className="text-amber-700">
                                                {t('manager.modules.countingListCreation.bulk.nothingToCreate', {}, 'Ingen rader igjen å opprette telleliste for (alle ligger allerede på aktiv telleliste eller er fjernet).')}
                                            </p>
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Name field */}
                            <div>
                                <label htmlFor="cl-description" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('manager.modules.countingListCreation.modal.nameLabel', {}, 'Navn på telleliste (valgfritt)')}
                                </label>
                                <input
                                    id="cl-description"
                                    type="text"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder={t('manager.modules.countingListCreation.modal.namePlaceholder', {}, 'Autogenerert BatchID brukes om tomt')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                                        bg-white text-gray-900 placeholder-gray-400"
                                />
                            </div>

                            {/* Max lines per list */}
                            <div>
                                <label htmlFor="cl-max-lines" className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('manager.modules.countingListCreation.bulk.maxLinesLabel', {}, 'Maks linjer per telleliste (valgfritt)')}
                                </label>
                                <NumericInput
                                    id="cl-max-lines"
                                    mode="integer"
                                    value={maxLinesPerList}
                                    onChange={(e) => setMaxLinesPerList(e.target.value)}
                                    placeholder={t('manager.modules.countingListCreation.bulk.maxLinesPlaceholder', {}, 'Tomt = én liste per lager')}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm
                                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                                        bg-white text-gray-900 placeholder-gray-400"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    {t('manager.modules.countingListCreation.bulk.maxLinesHelp', {}, 'Store utvalg deles i flere lister med løpenummer i navnet, slik at de kan fordeles på flere operatører.')}
                                </p>
                                {preview && preview.totalRows > 0 && (
                                    <p className="mt-1 text-sm text-gray-700">
                                        {t('manager.modules.countingListCreation.bulk.listsToCreate', { '0': listsToCreate }, `Det opprettes ${listsToCreate} telleliste(r).`)}
                                    </p>
                                )}
                            </div>

                            {/* Operator assignment - searchable */}
                            <div ref={dropdownRef}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    {t('manager.modules.countingListCreation.modal.assignOperatorLabel', {}, 'Tildel operatør (valgfritt)')}
                                </label>

                                {selectedOperatorId ? (
                                    /* Selected operator display */
                                    <div className="flex items-center justify-between px-3 py-2 border border-green-300 bg-green-50 rounded-lg text-sm">
                                        <div className="flex items-center gap-2">
                                            <User className="w-4 h-4 text-green-600" />
                                            <span className="text-gray-900 font-medium">{selectedOperatorName}</span>
                                        </div>
                                        <button
                                            onClick={handleClearOperator}
                                            className="text-gray-400 hover:text-red-500 transition-colors"
                                            title={t('manager.modules.countingListCreation.modal.clearAssignment', {}, 'Fjern tildeling')}
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    /* Search input */
                                    <div className="relative">
                                        <div className="relative">
                                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                            <input
                                                ref={operatorSearchRef}
                                                type="text"
                                                value={operatorSearch}
                                                onChange={(e) => {
                                                    setOperatorSearch(e.target.value);
                                                    setShowOperatorDropdown(true);
                                                }}
                                                onFocus={() => setShowOperatorDropdown(true)}
                                                placeholder={t('manager.modules.countingListCreation.modal.operatorSearchPlaceholder', {}, 'Søk etter operatør-ID eller navn...')}
                                                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm
                                                    focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                                                    bg-white text-gray-900 placeholder-gray-400"
                                            />
                                        </div>

                                        {/* Dropdown results */}
                                        {showOperatorDropdown && (
                                            <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                                {/* "Not assigned" option */}
                                                <button
                                                    onClick={() => {
                                                        handleClearOperator();
                                                        setShowOperatorDropdown(false);
                                                    }}
                                                    className="w-full text-left px-3 py-2 text-sm text-gray-500 italic hover:bg-gray-50 border-b"
                                                >
                                                    {t('manager.modules.countingListCreation.modal.notAssigned', {}, 'Ikke tildelt (kun lagersjef/konsulent)')}
                                                </button>

                                                {filteredProfiles.length > 0 ? (
                                                    filteredProfiles.map(p => (
                                                        <button
                                                            key={p.profileID}
                                                            onClick={() => handleSelectOperator(p)}
                                                            className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 hover:text-blue-700 transition-colors flex items-center gap-2"
                                                        >
                                                            <User className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                                            <span className="font-medium">{p.userID}</span>
                                                            {p.profileName && (
                                                                <span className="text-gray-500">- {p.profileName}</span>
                                                            )}
                                                        </button>
                                                    ))
                                                ) : (
                                                    <div className="px-3 py-2 text-sm text-gray-400 italic">
                                                        {t('manager.modules.countingListCreation.modal.noOperatorsFound', {}, 'Ingen operatører funnet')}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                {!result && (
                    <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-lg">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg
                                hover:bg-gray-50 transition-colors"
                            disabled={isSubmitting}
                        >
                            {t('common.buttons.cancel', {}, 'Avbryt')}
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={!canSubmit}
                            className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg
                                hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors
                                flex items-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    {t('manager.modules.countingListCreation.modal.creating', {}, 'Oppretter...')}
                                </>
                            ) : (
                                <>
                                    <ClipboardList className="w-4 h-4" />
                                    {listsToCreate > 1
                                        ? t('manager.modules.countingListCreation.bulk.createButton', { '0': listsToCreate }, `Opprett ${listsToCreate} tellelister`)
                                        : t('manager.modules.countingListCreation.modal.title', {}, 'Opprett telleliste')}
                                </>
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Hjelpere
// ---------------------------------------------------------------------------

type TFn = (key: string, params?: Record<string, string | number>, fallback?: string) => string;

function buildLocalPreview(rows: ItemBinCountingRow[]): CountingListSelectionPreview {
    const byWarehouse = new Map<string, { rows: number; items: Set<string> }>();
    const allItems = new Set<string>();
    for (const row of rows) {
        allItems.add(row.itemNo);
        const entry = byWarehouse.get(row.warehouseID) ?? { rows: 0, items: new Set<string>() };
        entry.rows += 1;
        entry.items.add(row.itemNo);
        byWarehouse.set(row.warehouseID, entry);
    }
    const warehouses: CountingListWarehouseSummary[] = Array.from(byWarehouse.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([warehouseID, v]) => ({ warehouseID, rows: v.rows, uniqueItems: v.items.size }));
    return { totalRows: rows.length, uniqueItems: allItems.size, warehouses };
}

const WarehouseBreakdown: React.FC<{
    warehouses: CountingListWarehouseSummary[];
    t: TFn;
    formatNumber: (n: number) => string;
}> = ({ warehouses, t, formatNumber }) => (
    <table className="w-full text-xs mt-1">
        <thead>
            <tr className="text-gray-500">
                <th className="text-left font-medium py-1">{t('warehouse.general.warehouse', {}, 'Lager')}</th>
                <th className="text-right font-medium py-1">{t('manager.modules.countingListCreation.bulk.rows', {}, 'Rader')}</th>
                <th className="text-right font-medium py-1">{t('manager.modules.countingListCreation.bulk.items', {}, 'Varer')}</th>
            </tr>
        </thead>
        <tbody>
            {warehouses.map(w => (
                <tr key={w.warehouseID} className="border-t border-gray-200">
                    <td className="py-1 font-medium text-gray-800">{w.warehouseID}</td>
                    <td className="py-1 text-right">{formatNumber(w.rows)}</td>
                    <td className="py-1 text-right">{formatNumber(w.uniqueItems)}</td>
                </tr>
            ))}
        </tbody>
    </table>
);

const ResultView: React.FC<{
    result: CreateCountingListsResponse;
    t: TFn;
    formatNumber: (n: number) => string;
    onClose: () => void;
}> = ({ result, t, formatNumber, onClose }) => (
    <div className="flex flex-col items-center py-2 space-y-3">
        {result.success ? (
            <>
                <CheckCircle className="w-12 h-12 text-green-500" />
                <h3 className="text-lg font-medium text-gray-900">
                    {result.lists.length > 1
                        ? t('manager.modules.countingListCreation.bulk.successTitle', {}, 'Tellelister opprettet')
                        : t('manager.modules.countingListCreation.modal.successTitle', {}, 'Telleliste opprettet!')}
                </h3>
            </>
        ) : (
            <>
                <AlertCircle className="w-12 h-12 text-amber-500" />
                <h3 className="text-lg font-medium text-gray-900">
                    {t('manager.modules.countingListCreation.bulk.partialTitle', {}, 'Opprettelsen stoppet underveis')}
                </h3>
                {result.errorMessage && (
                    <p className="text-sm text-red-700 text-center">{result.errorMessage}</p>
                )}
            </>
        )}

        <p className="text-sm text-gray-600">
            {t('manager.modules.countingListCreation.bulk.totalLinesCreated',
                { '0': formatNumber(result.totalLinesCreated), '1': result.lists.length },
                `${formatNumber(result.totalLinesCreated)} tellelinjer opprettet fordelt på ${result.lists.length} liste(r)`)}
        </p>

        {result.lists.length > 0 && (
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-gray-500 text-xs">
                        <th className="text-left font-medium py-1">{t('manager.modules.countingListCreation.modal.batchIdLabel', {}, 'Batch-ID:')}</th>
                        <th className="text-left font-medium py-1">{t('warehouse.general.warehouse', {}, 'Lager')}</th>
                        <th className="text-right font-medium py-1">{t('manager.modules.countingListCreation.bulk.lines', {}, 'Linjer')}</th>
                    </tr>
                </thead>
                <tbody>
                    {result.lists.map((l: CreatedCountingListInfo) => (
                        <tr key={l.batchID} className="border-t border-gray-200">
                            <td className="py-1">
                                <span className="font-mono font-medium">{l.batchID}</span>
                                {l.description && l.description !== l.batchID && (
                                    <span className="block text-xs text-gray-500">{l.description}</span>
                                )}
                            </td>
                            <td className="py-1">{l.warehouse}</td>
                            <td className="py-1 text-right">{formatNumber(l.linesCreated)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        )}

        <button
            onClick={onClose}
            className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
            {t('common.buttons.close', {}, 'Lukk')}
        </button>
    </div>
);
